import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ProjectoEntryKind, ProjectoPLItem, ProjectoPLItemInsert, ProjectoPLSummary } from '../../types/projecto';
import {
  projectoPLService,
  getItemCostRows,
  getItemEntryKind,
  getItemFigures,
  chargeFromCommission,
  commissionPctOf,
} from '../../services/projectoService';
import { useModal } from '../../contexts/ModalContext';
import { formatEuro } from '../../utils/currency';
import { FormBlock } from '../ui/FormBlock';
import { RowList } from '../ui/ListRows';
import { ProjectoPLItemModal } from './ProjectoPLItemModal';
import { ProjectoPLRow, PL_GRID, type PLRowVariant } from './ProjectoPLRow';
import { ProjectoBillingBlock, type BillingTerms } from './ProjectoBillingBlock';
import { log } from '../../lib/logger';

interface Props {
  projectoId: string;
  items: ProjectoPLItem[];
  onItemsChange: (items: ProjectoPLItem[]) => void;
  terms: BillingTerms;
  summary: ProjectoPLSummary;
  onTermsChange: (updates: Partial<BillingTerms>) => void;
}

const newRowId = () => `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Column titles and totals share the rows' grid so the amounts line up. */
function LineHeader({ variant, first }: { variant: PLRowVariant; first: string }) {
  const cell = 'text-[11px] font-medium text-gray-500';
  return (
    <div className={`hidden sm:grid ${PL_GRID[variant]} gap-x-3 pl-5 pr-2 pb-1.5`}>
      <span className={cell}>{first}</span>
      {variant === 'billed' && <>
        <span className={`${cell} text-right`}>Custo</span>
        <span className={`${cell} text-right`}>Comissão</span>
        <span className={`${cell} text-right`}>Cobrado</span>
        <span className={`${cell} text-right`}>Pagamento</span>
      </>}
      {variant === 'cost' && <>
        <span className={`${cell} text-right`}>Custo</span>
        <span className={`${cell} text-right`}>Pagamento</span>
      </>}
      {variant === 'service' && <span className={`${cell} text-right`}>Valor</span>}
    </div>
  );
}

function LineTotal({ variant, children }: { variant: PLRowVariant; children: ReactNode }) {
  return (
    <div className={`grid grid-cols-2 ${PL_GRID[variant]} items-baseline gap-x-3 pl-5 pr-2 pt-2.5 text-sm`}>
      <span className="font-semibold text-gray-900">Total</span>
      {children}
    </div>
  );
}

const TotalCell = ({ label, value, className = '' }: { label: string; value: number; className?: string }) => (
  <span className={`text-right tabular-nums font-bold max-sm:col-span-2 max-sm:flex max-sm:justify-between ${className}`}>
    <span className="sm:hidden font-normal text-gray-500">{label}</span>
    {formatEuro(value)}
  </span>
);

export function ProjectoPLTab({ projectoId, items, onItemsChange, terms, summary, onTermsChange }: Props) {
  const [editor, setEditor] = useState<{ item: ProjectoPLItem | null; kind: ProjectoEntryKind } | null>(null);
  const [quickName, setQuickName] = useState('');
  const [quickCost, setQuickCost] = useState('');
  const quickNameRef = useRef<HTMLInputElement>(null);
  const { showConfirm, toast } = useModal();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fixedBudget = terms.budget_enabled;
  const commissionPct = terms.default_commission_pct;

  const services = useMemo(() => items.filter((i) => getItemEntryKind(i) === 'revenue'), [items]);
  const suppliers = useMemo(() => items.filter((i) => getItemEntryKind(i) === 'passthrough'), [items]);
  const internal = useMemo(() => items.filter((i) => getItemEntryKind(i) === 'cost'), [items]);
  const allCosts = useMemo(() => items.filter((i) => getItemEntryKind(i) !== 'revenue'), [items]);

  const offCommission = useMemo(() => suppliers.filter((i) => {
    const { cost, charge } = getItemFigures(i);
    return cost > 0 && Math.abs(commissionPctOf(cost, charge) - commissionPct) > 0.01;
  }), [suppliers, commissionPct]);

  const toast500 = (action: string, err: unknown) => {
    const message = err instanceof Error ? err.message : String(err);
    log.error(`[ProjectoPLTab] ${action}:`, err);
    toast.error(`${action}: ${message}`);
  };

  const handleSave = async (data: Omit<ProjectoPLItemInsert, 'projecto_id'>, existing: ProjectoPLItem | null) => {
    try {
      if (existing) {
        const updated = await projectoPLService.update(existing.id, projectoId, data);
        onItemsChange(items.map((i) => (i.id === existing.id ? updated : i)));
      } else {
        const created = await projectoPLService.create({ ...data, projecto_id: projectoId, display_order: items.length });
        onItemsChange([...items, created]);
      }
    } catch (err) {
      toast500('Não foi possível guardar a linha', err);
      throw err; // keeps the editor open so what was typed is not lost
    }
  };

  const quickCostValue = Math.max(0, parseFloat(quickCost.replace(',', '.')) || 0);
  const quickCharge = fixedBudget ? quickCostValue : chargeFromCommission(quickCostValue, commissionPct);

  /** Name and cost is all a line needs; the rest can wait. */
  const handleQuickAdd = async () => {
    const name = quickName.trim();
    if (!name) { quickNameRef.current?.focus(); return; }
    try {
      const created = await projectoPLService.create({
        projecto_id: projectoId,
        entry_kind: 'passthrough',
        is_revenue: false,
        cost_billing_mode: 'reimbursed',
        name,
        quantity: 1,
        unit_price: quickCharge,
        passthrough_breakdown: quickCostValue > 0
          ? [{ id: newRowId(), label: '', amount: quickCostValue, quantity: 1, unit_cost: quickCostValue, paid: false }]
          : [],
        display_order: items.length,
      });
      onItemsChange([...items, created]);
      setQuickName('');
      setQuickCost('');
      quickNameRef.current?.focus();
    } catch (err) {
      toast500('Não foi possível adicionar a linha', err);
    }
  };

  const handleDelete = async (item: ProjectoPLItem) => {
    const confirmed = await showConfirm({
      title: 'Eliminar linha?',
      message: `Remover "${item.name}" do P&L? Esta ação não pode ser revertida.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await projectoPLService.delete(item.id, projectoId);
      onItemsChange(items.filter((i) => i.id !== item.id));
    } catch (err) {
      toast500('Não foi possível eliminar a linha', err);
    }
  };

  const handleDuplicate = async (item: ProjectoPLItem) => {
    try {
      const created = await projectoPLService.create({
        projecto_id: projectoId,
        item_type: item.item_type,
        entry_kind: getItemEntryKind(item),
        is_revenue: item.is_revenue,
        // The copy has not been paid yet
        passthrough_breakdown: getItemCostRows(item).map((c) => ({ ...c, id: newRowId(), paid: false })),
        name: `${item.name} (cópia)`,
        description: item.description,
        quantity: item.quantity,
        unit_price: getItemEntryKind(item) === 'cost' ? 0 : item.unit_price,
        currency: item.currency,
        cost_billing_mode: item.cost_billing_mode,
        scheduled_date: item.scheduled_date,
        vendor_name: item.vendor_name,
        vendor_contact: item.vendor_contact,
        contract_ref: item.contract_ref,
        link: item.link,
        observations: item.observations,
        display_order: items.length,
      });
      onItemsChange([...items, created]);
    } catch (err) {
      toast500('Não foi possível duplicar a linha', err);
    }
  };

  const handleApplyCommission = async () => {
    const before = offCommission.reduce((s, i) => s + getItemFigures(i).charge, 0);
    const after = offCommission.reduce((s, i) => s + chargeFromCommission(getItemFigures(i).cost, commissionPct), 0);
    const confirmed = await showConfirm({
      title: `Aplicar ${commissionPct}% de comissão?`,
      message: `O valor cobrado em ${offCommission.length === 1 ? '1 linha' : `${offCommission.length} linhas`} passa de ${formatEuro(before)} para ${formatEuro(after)}. As linhas sem custo ficam como estão.`,
      confirmText: 'Aplicar',
      cancelText: 'Cancelar',
      variant: 'warning',
    });
    if (!confirmed) return;
    try {
      const updated = await Promise.all(offCommission.map((i) =>
        projectoPLService.update(i.id, projectoId, { quantity: 1, unit_price: chargeFromCommission(getItemFigures(i).cost, commissionPct) })
      ));
      const byId = new Map(updated.map((i) => [i.id, i]));
      onItemsChange(items.map((i) => byId.get(i.id) ?? i));
      toast.success('Comissão aplicada');
    } catch (err) {
      toast500('Não foi possível aplicar a comissão', err);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from === -1 || to === -1) return;

    const reordered = arrayMove(items, from, to);
    onItemsChange(reordered);
    try {
      await projectoPLService.updateOrder(reordered.map((item, idx) => ({ id: item.id, display_order: idx })));
    } catch {
      toast.error('Não foi possível guardar a ordem');
    }
  };

  const renderRows = (rows: ProjectoPLItem[], variant: PLRowVariant) => (
    <RowList>
      <SortableContext items={rows.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        {rows.map((item) => (
          <ProjectoPLRow
            key={item.id}
            item={item}
            variant={variant}
            onOpen={(it) => setEditor({ item: it, kind: getItemEntryKind(it) })}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
          />
        ))}
      </SortableContext>
    </RowList>
  );

  const quickAdd = (
    <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="pl-quick-add">
      <input
        ref={quickNameRef}
        value={quickName}
        onChange={(e) => setQuickName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAdd(); }}
        placeholder="Nova linha: videógrafo, bailarina, aluguer de estúdio..."
        aria-label="Nome da nova linha"
        className="cm-input !h-9 flex-1 min-w-[12rem] basis-full sm:basis-0"
      />
      <label className="cm-affix has-right w-32">
        <input
          inputMode="decimal"
          value={quickCost}
          onChange={(e) => setQuickCost(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAdd(); }}
          placeholder="Custo"
          aria-label="Custo da nova linha"
          className="cm-input cm-numeric !h-9"
        />
        <span>€</span>
      </label>
      {!fixedBudget && quickCostValue > 0 && (
        <span className="text-xs text-gray-500 whitespace-nowrap">
          + {commissionPct}% = <strong className="text-gray-900 tabular-nums">{formatEuro(quickCharge)}</strong>
        </span>
      )}
      <button type="button" onClick={handleQuickAdd} className="cm-btn cm-btn-primary !h-9 !px-4 ml-auto sm:ml-0">
        <Plus className="w-4 h-4" /> Adicionar
      </button>
      <button type="button" onClick={() => setEditor({ item: null, kind: 'passthrough' })} className="text-xs font-medium text-gray-600 hover:text-gray-900 underline underline-offset-2">
        Com mais detalhes
      </button>
    </div>
  );

  const addButton = (label: string, kind: ProjectoEntryKind) => (
    <button type="button" onClick={() => setEditor({ item: null, kind })} className="cm-btn-add !py-1.5 mt-3">
      <Plus className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-7 pb-10 pt-2">
      <ProjectoBillingBlock
        terms={terms}
        summary={summary}
        linesOffCommission={offCommission.length}
        onChange={onTermsChange}
        onApplyCommission={handleApplyCommission}
      />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {fixedBudget ? (
          <FormBlock title="Custos do projeto">
            {allCosts.length === 0 ? (
              <p className="text-sm text-gray-500 pb-1">
                Regista aqui tudo o que a agência paga: pessoas, serviços, espaços, materiais. Quanto sobra do orçamento é a margem.
              </p>
            ) : (
              <>
                <LineHeader variant="cost" first="Linha" />
                {renderRows(allCosts, 'cost')}
                <LineTotal variant="cost"><TotalCell label="Custos" value={summary.costs} /></LineTotal>
              </>
            )}
            {quickAdd}
          </FormBlock>
        ) : (
          <>
            <FormBlock title="Fornecedores e talento">
              {suppliers.length === 0 ? (
                <p className="text-sm text-gray-500 pb-1">
                  Quem a agência paga e cobra ao cliente com comissão. Indica o nome e o custo: o valor cobrado é calculado.
                </p>
              ) : (
                <>
                  <LineHeader variant="billed" first="Linha" />
                  {renderRows(suppliers, 'billed')}
                  <LineTotal variant="billed">
                    <TotalCell label="Custo" value={summary.supplierCosts} />
                    <TotalCell label="Comissão" value={summary.commission} className={summary.commission < 0 ? 'text-red-700' : 'text-emerald-700'} />
                    <TotalCell label="Cobrado" value={summary.supplierCharged} />
                  </LineTotal>
                </>
              )}
              {quickAdd}
            </FormBlock>

            <FormBlock title="Serviços próprios">
              {services.length === 0 ? (
                <p className="text-sm text-gray-500">O trabalho da própria agência (gestão de redes, produção). Não tem fornecedor: o valor é todo margem.</p>
              ) : (
                <>
                  <LineHeader variant="service" first="Serviço" />
                  {renderRows(services, 'service')}
                  <LineTotal variant="service"><TotalCell label="Valor" value={summary.ownServices} /></LineTotal>
                </>
              )}
              {addButton('Serviço próprio', 'revenue')}
            </FormBlock>

            <FormBlock title="Custos internos">
              {internal.length === 0 ? (
                <p className="text-sm text-gray-500">O que a agência paga e não cobra ao cliente (ex.: uma oferta ou um custo assumido). Reduz a margem.</p>
              ) : (
                <>
                  <LineHeader variant="cost" first="Linha" />
                  {renderRows(internal, 'cost')}
                  <LineTotal variant="cost"><TotalCell label="Custo" value={summary.internalCosts} /></LineTotal>
                </>
              )}
              {addButton('Custo interno', 'cost')}
            </FormBlock>
          </>
        )}

        {/* With a fixed budget these don't add to what the client pays */}
        {fixedBudget && services.length > 0 && (
          <FormBlock title="Serviços próprios">
            <p className="text-xs text-gray-500 mb-2">Incluídos no orçamento: não somam ao valor que o cliente paga.</p>
            <LineHeader variant="service" first="Serviço" />
            {renderRows(services, 'service')}
          </FormBlock>
        )}
      </DndContext>

      {editor && (
        <ProjectoPLItemModal
          item={editor.item}
          defaultKind={editor.kind}
          fixedBudget={fixedBudget}
          defaultCommissionPct={commissionPct}
          onSave={(data) => handleSave(data, editor.item)}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
}
